import { useMemo, useState } from "react";
import { TASKS, TASK_STATUS_META, TASK_PRIORITY_META } from "./phase1Data";
import { SectionHeader } from "./ui";
import { LiveBadge } from "./ui-badges";
import { Calendar, Plus, CheckCircle2, Circle, Clock, AlertOctagon } from "lucide-react";

const TONE_BG = {
  slate: "bg-slate-100 text-slate-700 ring-slate-500/20",
  sky: "bg-sky-50 text-sky-700 ring-sky-600/20",
  emerald: "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
  rose: "bg-rose-50 text-rose-700 ring-rose-600/20",
  amber: "bg-amber-50 text-amber-800 ring-amber-600/30",
};

const STATUS_ICON = {
  open: Circle,
  in_progress: Clock,
  done: CheckCircle2,
  blocked: AlertOctagon,
};

const today = new Date("2026-02-06");

const dayDelta = (iso) => {
  const d = new Date(iso);
  const ms = d.getTime() - today.getTime();
  return Math.round(ms / (1000 * 60 * 60 * 24));
};

const formatDue = (iso) => {
  const delta = dayDelta(iso);
  const d = new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
  if (delta < 0) return { text: `${d} · ${Math.abs(delta)}d overdue`, tone: "rose" };
  if (delta === 0) return { text: `${d} · Today`, tone: "amber" };
  if (delta <= 3) return { text: `${d} · in ${delta}d`, tone: "amber" };
  return { text: `${d} · in ${delta}d`, tone: "slate" };
};

const FILTERS = [
  { key: "all", label: "All" },
  { key: "open", label: "Open" },
  { key: "in_progress", label: "In progress" },
  { key: "done", label: "Done" },
];

export const FollowUpTasks = () => {
  const [filter, setFilter] = useState("all");

  const visible = useMemo(() => {
    if (filter === "all") return TASKS;
    return TASKS.filter((t) => t.status === filter);
  }, [filter]);

  const stats = useMemo(() => {
    return {
      open: TASKS.filter((t) => t.status === "open").length,
      in_progress: TASKS.filter((t) => t.status === "in_progress").length,
      done: TASKS.filter((t) => t.status === "done").length,
      overdue: TASKS.filter((t) => t.status !== "done" && dayDelta(t.due) < 0).length,
    };
  }, []);

  return (
    <div data-testid="follow-up-tasks">
      <SectionHeader
        title="Follow-up tasks"
        subtitle="Action items across the pipeline and active customers."
        right={
          <div className="flex items-center gap-3">
            <LiveBadge live={false} />
            <button
              type="button"
              data-testid="new-task-btn"
              onClick={(e) => e.preventDefault()}
              className="inline-flex items-center gap-1.5 rounded-lg bg-[#0d3b66] text-white px-3 py-1.5 text-sm font-semibold hover:bg-[#0a2e52] transition-colors"
            >
              <Plus className="h-4 w-4" /> New task
            </button>
          </div>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <Stat tone="slate" label="Open" value={stats.open} />
        <Stat tone="sky" label="In progress" value={stats.in_progress} />
        <Stat tone="emerald" label="Done" value={stats.done} />
        <Stat tone="rose" label="Overdue" value={stats.overdue} />
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="px-3 py-2 border-b border-slate-100 flex gap-1">
          {FILTERS.map((f) => (
            <button
              type="button"
              key={f.key}
              data-testid={`task-filter-${f.key}`}
              onClick={() => setFilter(f.key)}
              className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                filter === f.key
                  ? "bg-[#0d3b66] text-white"
                  : "text-slate-600 hover:bg-slate-100"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <table className="w-full text-sm" data-testid="tasks-table">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left px-4 py-2.5">Task</th>
              <th className="text-left px-4 py-2.5">Target</th>
              <th className="text-left px-4 py-2.5">Owner</th>
              <th className="text-left px-4 py-2.5">Due</th>
              <th className="text-left px-4 py-2.5">Priority</th>
              <th className="text-left px-4 py-2.5">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {visible.map((t) => {
              const Icon = STATUS_ICON[t.status];
              const due = formatDue(t.due);
              const statusMeta = TASK_STATUS_META[t.status];
              const prioMeta = TASK_PRIORITY_META[t.priority];
              return (
                <tr key={t.id} className="hover:bg-slate-50" data-testid={`task-row-${t.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-start gap-2">
                      <Icon
                        className={`h-4 w-4 mt-0.5 shrink-0 ${
                          t.status === "done"
                            ? "text-emerald-600"
                            : t.status === "in_progress"
                            ? "text-sky-600"
                            : "text-slate-400"
                        }`}
                      />
                      <span className={`font-medium ${t.status === "done" ? "text-slate-400 line-through" : "text-slate-900"}`}>
                        {t.title}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-700">{t.target}</td>
                  <td className="px-4 py-3 text-slate-700">{t.owner}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded ring-1 ring-inset ${TONE_BG[due.tone]}`}>
                      <Calendar className="h-3 w-3" />
                      {due.text}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded ring-1 ring-inset ${TONE_BG[prioMeta.tone]}`}>
                      {prioMeta.label}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded ring-1 ring-inset ${TONE_BG[statusMeta.tone]}`}>
                      {statusMeta.label}
                    </span>
                  </td>
                </tr>
              );
            })}
            {visible.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-sm text-slate-500">
                  No tasks match this filter.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const Stat = ({ tone, label, value }) => (
  <div className={`rounded-lg border border-slate-200 bg-white p-4`}>
    <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
    <div className={`mt-1 text-2xl font-semibold tabular-nums ${tone === "rose" ? "text-rose-600" : "text-slate-900"}`}>
      {value}
    </div>
  </div>
);
